import {
    makeStyles,
    tokens,
    Button,
    Spinner,
    Badge,
    ProgressBar,
    MessageBar,
    MessageBarBody,
} from '@fluentui/react-components';
import { Play24Regular } from '@fluentui/react-icons';
import type { SearchIndexerContextValue, SourceRunProgress } from '../../hooks/useSearchIndexer';
import { SEARCH_INDEX_NO_CONTENT_WARNING_MIN_ITEMS } from '../../config/constants';

const useStyles = makeStyles({
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    heading: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        margin: 0,
    },
    sourceRow: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    sourceHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    sourceName: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    stats: {
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
    },
    statChip: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    statNum: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '12px',
        padding: '16px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '8px',
    },
    summaryItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    summaryLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    summaryValue: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
    },
});

function statusBadge(status: SourceRunProgress['status']) {
    switch (status) {
        case 'completed':
            return <Badge color="success" appearance="filled">Completed</Badge>;
        case 'running':
            return <Badge color="informative" appearance="filled">Running</Badge>;
        case 'error':
            return <Badge color="danger" appearance="filled">Error</Badge>;
        case 'busy':
            return <Badge color="warning" appearance="filled">Locked</Badge>;
        default:
            return <Badge appearance="outline">Pending</Badge>;
    }
}

interface Props {
    ctx: SearchIndexerContextValue;
}

export function IndexProgressPanel({ ctx }: Props) {
    const styles = useStyles();
    const {
        sources,
        workerReady,
        workerLoading,
        isRunning,
        runProgress,
        runSummaries,
        startFullIndexRun,
    } = ctx;

    const totalItems = runProgress.reduce((sum, p) => sum + p.total, 0);
    const totalProcessed = runProgress.reduce((sum, p) => sum + p.processed, 0);
    const overallPct = totalItems > 0 ? totalProcessed / totalItems : 0;

    const pageSourceMissingTag = sources.some(
        s => s.sourceType === 'Page' && !s.tagFieldName,
    );

    const totalNew = runSummaries.reduce((s, r) => s + r.newlyIndexed, 0);
    const totalCacheHits = runSummaries.reduce((s, r) => s + r.cacheHits, 0);
    const totalRegen = runSummaries.reduce((s, r) => s + r.regenerated, 0);
    const totalSkipped = runSummaries.reduce((s, r) => s + r.unpublishedSkipped, 0);
    const totalWriteFailures = runSummaries.reduce((s, r) => s + (r.chunkWriteFailures ?? 0), 0);

    return (
        <div className={styles.card}>
            <div className={styles.topBar}>
                <h3 className={styles.heading}>Full Index Run</h3>
                <Button
                    icon={isRunning ? <Spinner size="tiny" /> : <Play24Regular />}
                    appearance="primary"
                    onClick={startFullIndexRun}
                    disabled={
                        !workerReady ||
                        workerLoading ||
                        isRunning ||
                        sources.length === 0 ||
                        pageSourceMissingTag
                    }
                >
                    {isRunning ? 'Indexing…' : 'Run Full Index'}
                </Button>
            </div>

            {!workerReady && !workerLoading && (
                <MessageBar intent="warning">
                    <MessageBarBody>
                        Indexer engine failed to load. Check the Status tab for details.
                    </MessageBarBody>
                </MessageBar>
            )}

            {sources.length === 0 && (
                <MessageBar intent="info">
                    <MessageBarBody>
                        No sources registered. Go to the Sources tab to register one.
                    </MessageBarBody>
                </MessageBar>
            )}

            {sources.length > 0 && pageSourceMissingTag && (
                <MessageBar intent="warning">
                    <MessageBarBody>
                        The Site Pages source needs a Tag Field before indexing can start. Set it
                        from the Sources tab.
                    </MessageBarBody>
                </MessageBar>
            )}

            {isRunning && totalItems > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <ProgressBar value={overallPct} />
                    <span style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
                        {totalProcessed} / {totalItems} items processed
                    </span>
                </div>
            )}

            {runProgress.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {runProgress.map(p => (
                        <div key={p.sourceListId} className={styles.sourceRow}>
                            <div className={styles.sourceHeader}>
                                <span className={styles.sourceName}>{p.displayName}</span>
                                {statusBadge(p.status)}
                            </div>

                            {p.status === 'running' && p.total > 0 && (
                                <ProgressBar value={p.total > 0 ? p.processed / p.total : 0} />
                            )}

                            <div className={styles.stats}>
                                {p.total > 0 && (
                                    <span className={styles.statChip}>
                                        Items: <span className={styles.statNum}>{p.processed}/{p.total}</span>
                                    </span>
                                )}
                                {p.newlyIndexed > 0 && (
                                    <span className={styles.statChip}>
                                        New: <span className={styles.statNum}>{p.newlyIndexed}</span>
                                    </span>
                                )}
                                {p.regenerated > 0 && (
                                    <span className={styles.statChip}>
                                        Regenerated: <span className={styles.statNum}>{p.regenerated}</span>
                                    </span>
                                )}
                                {p.cacheHits > 0 && (
                                    <span className={styles.statChip}>
                                        Cache hits: <span className={styles.statNum}>{p.cacheHits}</span>
                                    </span>
                                )}
                                {(p.noContentSkipped ?? 0) > 0 && (
                                    <span className={styles.statChip}>
                                        No content: <span className={styles.statNum}>{p.noContentSkipped}</span>
                                    </span>
                                )}
                                {(p.chunkWriteFailures ?? 0) > 0 && (
                                    <span className={styles.statChip} style={{ color: tokens.colorPaletteRedForeground1 }}>
                                        Write failed: <span className={styles.statNum} style={{ color: tokens.colorPaletteRedForeground1 }}>{p.chunkWriteFailures}</span>
                                    </span>
                                )}
                                {p.errorMessage && (
                                    <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>
                                        {p.errorMessage}
                                    </span>
                                )}
                            </div>

                            {(p.chunkWriteFailures ?? 0) > 0 && (
                                <MessageBar intent="error">
                                    <MessageBarBody>
                                        {p.chunkWriteFailures} chunk{p.chunkWriteFailures > 1 ? 's' : ''} failed to save to
                                        the {p.displayName} embedding cache list. Items were processed but not actually
                                        indexed. Check the browser console for the underlying Graph error (logged per
                                        failed write), and verify the cache list's column types weren't left over from an
                                        older schema version.
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            {(() => {
                                const processedForContent = (p.newlyIndexed ?? 0) + (p.regenerated ?? 0);
                                const noContentSkipped = p.noContentSkipped ?? 0;
                                // Only warn once there's a large enough sample that a 100% no-content
                                // rate is more likely a systemic extraction problem than a handful of
                                // genuinely thin pages — and scope the message to that sample (new/
                                // regenerated items), not the source's full item count, which usually
                                // includes many drafts/unchanged items that were never even attempted.
                                if (
                                    p.status !== 'completed' ||
                                    processedForContent < SEARCH_INDEX_NO_CONTENT_WARNING_MIN_ITEMS ||
                                    noContentSkipped !== processedForContent
                                ) {
                                    return null;
                                }
                                return (
                                    <MessageBar intent="warning">
                                        <MessageBarBody>
                                            All {processedForContent} newly indexed/regenerated items in {p.displayName} this
                                            run produced no extractable content. This usually means the page canvas content
                                            isn't in the expected format. Check the browser console for details.
                                        </MessageBarBody>
                                    </MessageBar>
                                );
                            })()}
                        </div>
                    ))}
                </div>
            )}

            {runSummaries.length > 0 && !isRunning && (
                <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Newly Indexed</span>
                        <span className={styles.summaryValue} style={{ color: tokens.colorPaletteLightGreenForeground1 }}>
                            {totalNew}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Regenerated</span>
                        <span className={styles.summaryValue} style={{ color: tokens.colorPaletteMarigoldForeground1 }}>
                            {totalRegen}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Cache Hits</span>
                        <span className={styles.summaryValue} style={{ color: tokens.colorNeutralForeground2 }}>
                            {totalCacheHits}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Skipped (Draft)</span>
                        <span className={styles.summaryValue} style={{ color: tokens.colorNeutralForeground3 }}>
                            {totalSkipped}
                        </span>
                    </div>
                    {totalWriteFailures > 0 && (
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Write Failures</span>
                            <span className={styles.summaryValue} style={{ color: tokens.colorPaletteRedForeground1 }}>
                                {totalWriteFailures}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
